import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CalendarMonth,
  EventAvailable,
  Schedule,
  AccessTime,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format, parseISO, addDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import EmployeeLayout from '../components/layout/EmployeeLayout';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { useEmployeeSchedules, EmployeeShift } from '../hooks/useEmployeeSchedules';
import { useEmployees } from '../hooks/useEmployees';
import { useShops } from '../hooks/useShops';
import EmployeeWelcomeBanner from '../components/onboarding/EmployeeWelcomeBanner';

export default function EmployeeDashboardPage() {
  const { user } = useAuth();
  const { organization } = useOrganization();
  const { employees } = useEmployees();
  const { shops } = useShops();
  const { enqueueSnackbar } = useSnackbar();

  // Get shop by ID
  const getShop = (shopId: string) => {
    return shops.find(s => s.id === shopId);
  };

  // Get shop name by ID
  const getShopName = (shopId: string) => {
    const shop = getShop(shopId);
    return shop?.name || 'Unknown Shop';
  };

  // Welcome banner state - show if not dismissed
  const [showWelcomeBanner, setShowWelcomeBanner] = useState(() => {
    return !user?.welcomeBannerDismissed;
  });

  const handleBannerDismiss = () => {
    setShowWelcomeBanner(false);
    enqueueSnackbar("Check your schedule below or set your availability in the menu.", {
      variant: "info",
      autoHideDuration: 5000,
    });
  };

  // Find the current employee record
  const currentEmployee = useMemo(() => {
    if (!user?.employeeId) return null;
    return employees.find(emp => emp.id === user.employeeId);
  }, [user?.employeeId, employees]);

  // Get schedules for next 7 days
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const next7DaysEnd = addDays(today, 6);

  const {
    schedules,
    isLoading: schedulesLoading,
  } = useEmployeeSchedules(
    user?.employeeId,
    format(today, 'yyyy-MM-dd'),
    format(next7DaysEnd, 'yyyy-MM-dd')
  );

  // Calculate next 7 days schedule stats
  const next7DaysStats = useMemo((): { shifts: number; totalHours: number; nextShift: EmployeeShift | null } => {
    if (!schedules) return { shifts: 0, totalHours: 0, nextShift: null };

    const totalHours = schedules.reduce((acc, shift) => {
      // Calculate hours from start and end times
      if (shift.startTime && shift.endTime) {
        const [startH, startM] = shift.startTime.split(':').map(Number);
        const [endH, endM] = shift.endTime.split(':').map(Number);
        let hours = endH - startH + (endM - startM) / 60;
        if (hours < 0) hours += 24; // Handle overnight shifts
        return acc + hours;
      }
      return acc;
    }, 0);

    // Find next upcoming shift (today or later)
    const upcomingShifts = schedules
      .sort((a, b) => parseISO(a.date).getTime() - parseISO(b.date).getTime());

    return {
      shifts: schedules.length,
      totalHours: Math.round(totalHours * 10) / 10,
      nextShift: upcomingShifts[0] || null,
    };
  }, [schedules]);

  // Get next 7 days for quick view
  const next7Days = useMemo(() => {
    const days: Array<{
      date: Date;
      dateStr: string;
      shift: EmployeeShift | undefined;
      isToday: boolean;
    }> = [];
    for (let i = 0; i < 7; i++) {
      const date = addDays(today, i);
      const dateStr = format(date, 'yyyy-MM-dd');
      const shift = schedules?.find(s => s.date === dateStr);
      days.push({
        date,
        dateStr,
        shift,
        isToday: i === 0,
      });
    }
    return days;
  }, [today, schedules]);

  if (!currentEmployee && !schedulesLoading) {
    return (
      <EmployeeLayout pageTitle="Dashboard">
        <Alert severity="warning" sx={{ mt: 2 }}>
          Your employee profile could not be found. Please contact your manager.
        </Alert>
      </EmployeeLayout>
    );
  }

  return (
    <EmployeeLayout pageTitle="Dashboard">
      <Box sx={{ mb: { xs: 2, sm: 4 } }}>
        <Typography
          variant="h4"
          fontWeight={600}
          gutterBottom
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
        >
          Welcome back, {user?.displayName || 'Employee'}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {organization?.name} - Here's your schedule overview
        </Typography>
      </Box>

      {/* Welcome Banner for new employees */}
      {showWelcomeBanner && (
        <Box sx={{ mb: { xs: 2, sm: 0 } }}>
          <EmployeeWelcomeBanner onDismiss={handleBannerDismiss} />
        </Box>
      )}

      {/* Inactive Status Banner */}
      {currentEmployee?.status === 'inactive' && (
        <Alert
          severity="info"
          icon={<InfoIcon />}
          sx={{ mb: { xs: 2, sm: 3 } }}
        >
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            You are currently marked as inactive
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            You won't receive new schedule assignments. If you believe this is a mistake, please contact your manager.
          </Typography>
        </Alert>
      )}

      {schedulesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Stats Cards */}
          <Grid container spacing={{ xs: 2, sm: 3 }} sx={{ mb: { xs: 3, sm: 4 } }}>
            <Grid item xs={6} sm={6} md={3}>
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CalendarMonth sx={{ color: 'primary.main', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                    {next7DaysStats.shifts}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                  >
                    Shifts (Next 7 Days)
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={6} sm={6} md={3}>
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    bgcolor: 'success.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <AccessTime sx={{ color: 'success.main', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h4" fontWeight={600} sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                    {next7DaysStats.totalHours}h
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: { xs: '0.7rem', sm: '0.875rem' } }}
                  >
                    Hours (Next 7 Days)
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} sm={12} md={6}>
              <Paper
                sx={{
                  p: { xs: 2, sm: 3 },
                  display: 'flex',
                  alignItems: 'center',
                  gap: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                  height: '100%',
                }}
              >
                <Box
                  sx={{
                    width: { xs: 40, sm: 48 },
                    height: { xs: 40, sm: 48 },
                    borderRadius: 2,
                    bgcolor: 'info.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Schedule sx={{ color: 'info.main', fontSize: { xs: 20, sm: 24 } }} />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {next7DaysStats.nextShift ? (
                    (() => {
                      const shift = next7DaysStats.nextShift;
                      const [startH, startM] = shift.startTime.split(':').map(Number);
                      const [endH, endM] = shift.endTime.split(':').map(Number);
                      let hours = endH - startH + (endM - startM) / 60;
                      if (hours < 0) hours += 24;
                      const hoursDisplay = Math.round(hours * 10) / 10;
                      return (
                        <>
                          <Typography
                            variant="h6"
                            fontWeight={600}
                            sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                          >
                            {format(parseISO(shift.date), 'EEEE, MMM d')}
                          </Typography>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                          >
                            Next shift: {shift.startTime} - {shift.endTime} ({hoursDisplay}h)
                          </Typography>
                        </>
                      );
                    })()
                  ) : (
                    <>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ fontSize: { xs: '0.95rem', sm: '1.25rem' } }}
                      >
                        No Shifts Scheduled
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        No shifts in the next 7 days
                      </Typography>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* Next 7 Days Quick View */}
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: { xs: 2, sm: 3 } }}>
              <EventAvailable color="primary" sx={{ fontSize: { xs: 20, sm: 24 } }} />
              <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                Next 7 Days
              </Typography>
            </Box>

            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {next7Days.map(({ date, shift, isToday }) => {
                const shop = shift ? getShop(shift.shopId) : null;
                const shopColor = shop?.color || '#e0e0e0';

                return (
                  <Grid item xs={4} sm={3} md={12 / 7} key={date.toISOString()}>
                    <Card
                      variant="outlined"
                      sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        borderColor: isToday ? 'primary.main' : 'divider',
                        borderWidth: isToday ? 2 : 1,
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                    >
                      {/* Colored top accent bar */}
                      <Box
                        sx={{
                          height: { xs: 3, sm: 4 },
                          bgcolor: shift ? shopColor : 'grey.300',
                        }}
                      />

                      <CardContent sx={{ p: { xs: 1, sm: 1.5 }, '&:last-child': { pb: { xs: 1, sm: 1.5 } } }}>
                        {/* Date header */}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, mb: { xs: 0.5, sm: 1 } }}>
                          <Typography
                            variant="h5"
                            fontWeight={700}
                            color={isToday ? 'primary.main' : 'text.primary'}
                            sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                          >
                            {format(date, 'd')}
                          </Typography>
                          <Typography
                            variant="caption"
                            color={isToday ? 'primary.main' : 'text.secondary'}
                            fontWeight={isToday ? 600 : 400}
                            sx={{
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                              fontSize: { xs: '0.6rem', sm: '0.75rem' },
                            }}
                          >
                            {format(date, 'EEE')}
                          </Typography>
                        </Box>

                        {shift ? (
                          <Box>
                            {/* Shop name with colored dot */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Box
                                sx={{
                                  width: { xs: 6, sm: 8 },
                                  height: { xs: 6, sm: 8 },
                                  borderRadius: '50%',
                                  bgcolor: shopColor,
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: { xs: '0.65rem', sm: '0.875rem' },
                                }}
                              >
                                {getShopName(shift.shopId)}
                              </Typography>
                            </Box>

                            {/* Time with clock icon */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <AccessTime sx={{ fontSize: { xs: 10, sm: 14 }, color: 'text.secondary' }} />
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={500}
                                sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' } }}
                              >
                                {shift.startTime} - {shift.endTime}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              py: { xs: 0.5, sm: 1 },
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.disabled"
                              sx={{ fontStyle: 'italic', fontSize: { xs: '0.65rem', sm: '0.875rem' } }}
                            >
                              Day off
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Paper>
        </>
      )}
    </EmployeeLayout>
  );
}
